import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Typography,
  Switch,
  Form,
  Divider,
  Button,
  Spin,
  message,
  Space,
  Alert
} from 'antd';
import {
  BellOutlined,
  MailOutlined,
  ShoppingOutlined,
  CalendarOutlined,
  StarOutlined,
  SafetyCertificateOutlined,
  CrownOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useStores } from '../../stores';
import './NotificationPreferencesPage.scss';

const { Title, Text, Paragraph } = Typography;

const NotificationPreferencesPage = observer(() => {
  const navigate = useNavigate();
  const { notificationStore } = useStores();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  useEffect(() => {
    notificationStore.fetchPreferences();
  }, [notificationStore]);

  useEffect(() => {
    if (notificationStore.preferences) {
      form.setFieldsValue(notificationStore.preferences);
    }
  }, [notificationStore.preferences, form]);

  const handleSubmit = async (values) => {
    const result = await notificationStore.updatePreferences(values);
    if (result.success) {
      message.success(t('notifications.preferences.saveSuccess'));
    } else {
      message.error(result.error || t('notifications.preferences.saveFailed'));
    }
  };

  const preferenceCategories = [
    {
      key: 'orders',
      icon: <ShoppingOutlined />,
      title: t('notifications.preferences.orders.title'),
      description: t('notifications.preferences.orders.description'),
      field: 'email_order_updates'
    },
    {
      key: 'appointments',
      icon: <CalendarOutlined />,
      title: t('notifications.preferences.appointments.title'),
      description: t('notifications.preferences.appointments.description'),
      field: 'email_appointment_updates'
    },
    {
      key: 'suggestions',
      icon: <BellOutlined />,
      title: t('notifications.preferences.suggestions.title'),
      description: t('notifications.preferences.suggestions.description'),
      field: 'email_suggestion_updates'
    },
    {
      key: 'subscription',
      icon: <CrownOutlined />,
      title: t('notifications.preferences.subscription.title'),
      description: t('notifications.preferences.subscription.description'),
      field: 'email_subscription_alerts'
    },
    {
      key: 'reviews',
      icon: <StarOutlined />,
      title: t('notifications.preferences.reviews.title'),
      description: t('notifications.preferences.reviews.description'),
      field: 'email_review_notifications'
    },
    {
      key: 'kyc',
      icon: <SafetyCertificateOutlined />,
      title: t('notifications.preferences.kyc.title'),
      description: t('notifications.preferences.kyc.description'),
      field: 'email_kyc_updates'
    }
  ];

  if (notificationStore.loadingPreferences && !notificationStore.preferences) {
    return (
      <div className="notification-preferences-page">
        <Card>
          <div className="loading-container">
            <Spin size="large" />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="notification-preferences-page">
      <Card>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="back-button"
        >
          {t('common.back')}
        </Button>

        <Title level={2}>
          <BellOutlined /> {t('notifications.preferences.pageTitle')}
        </Title>

        <Paragraph type="secondary">
          {t('notifications.preferences.description')}
        </Paragraph>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            inapp_enabled: true,
            email_order_updates: true,
            email_appointment_updates: true,
            email_suggestion_updates: true,
            email_subscription_alerts: true,
            email_review_notifications: true,
            email_kyc_updates: true
          }}
        >
          <div className="preference-section">
            <Title level={4}>
              <BellOutlined /> {t('notifications.preferences.inAppSection')}
            </Title>

            <div className="preference-item">
              <div className="preference-info">
                <Text strong>{t('notifications.preferences.inApp.title')}</Text>
                <Text type="secondary">
                  {t('notifications.preferences.inApp.description')}
                </Text>
              </div>
              <Form.Item name="inapp_enabled" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </div>
          </div>

          <Divider />

          <div className="preference-section">
            <Title level={4}>
              <MailOutlined /> {t('notifications.preferences.emailSection')}
            </Title>

            <Alert
              message={t('notifications.preferences.emailNote')}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            {preferenceCategories.map((category) => (
              <div key={category.key} className="preference-item">
                <div className="preference-info">
                  <Space>
                    {category.icon}
                    <Text strong>{category.title}</Text>
                  </Space>
                  <Text type="secondary">{category.description}</Text>
                </div>
                <Form.Item name={category.field} valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>
            ))}
          </div>

          <Divider />

          <div className="form-actions">
            <Space>
              <Button onClick={() => navigate(-1)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={notificationStore.loadingPreferences}
              >
                {t('common.save')}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
});

export default NotificationPreferencesPage;
